import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Badge, Tabs, Tab, Modal, Form, Alert } from "react-bootstrap";
import { Package, Clock, CreditCard, Plus, Edit, Trash2, Globe, BookOpen } from "lucide-react";
import HeadingBanner from "../components/HeadingBanner";
import LeftPanel from "../components/LeftPanel";
import OrientationPrompt from "../components/OrientationPrompt";
import {
  whoami,
  getTrialDefinitions,
  createTrialDefinition,
  updateTrialDefinition,
  deleteTrialDefinition,
  getPurchaseOptions,
  createPurchaseOption,
  updatePurchaseOption,
  deletePurchaseOption,
  getCountries,
} from "../services/APIClient";
import "../components/ProductCatalog.css";

function ProductCatalog() {
  const [teacherid, setTeacherid] = useState(null);
  const [name, setName] = useState("Admin");
  const [activeTab, setActiveTab] = useState("trials");
  const [isPortrait, setIsPortrait] = useState(window.matchMedia("(orientation: portrait)").matches);

  // Trial Definitions State
  const [trialDefinitions, setTrialDefinitions] = useState([]);
  const [trialsLoading, setTrialsLoading] = useState(true);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [editingTrial, setEditingTrial] = useState(null);

  // Purchase Options State
  const [purchaseOptions, setPurchaseOptions] = useState([]);
  const [purchasesLoading, setPurchasesLoading] = useState(true);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);

  // Shared State
  const [countries, setCountries] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    whoami().then((response) => {
      setTeacherid(response.data.stakeholder.id);
      setName(response.data.stakeholder.name);
    });

    loadCountries();
    loadTrialDefinitions();
    loadPurchaseOptions();
  }, []);

  const loadCountries = async () => {
    try {
      const data = await getCountries();
      setCountries(data);
    } catch (err) {
      console.error("Error loading countries:", err);
    }
  };

  const loadTrialDefinitions = async () => {
    setTrialsLoading(true);
    try {
      const data = await getTrialDefinitions();
      setTrialDefinitions(data);
    } catch (err) {
      setError("Failed to load trial definitions");
      console.error(err);
    } finally {
      setTrialsLoading(false);
    }
  };

  const loadPurchaseOptions = async () => {
    setPurchasesLoading(true);
    try {
      const data = await getPurchaseOptions();
      setPurchaseOptions(data);
    } catch (err) {
      setError("Failed to load purchase options");
      console.error(err);
    } finally {
      setPurchasesLoading(false);
    }
  };

  const handleOpenTrialModal = (trial = null) => {
    setEditingTrial(trial);
    setShowTrialModal(true);
    setError(null);
    setSuccess(null);
  };

  const handleOpenPurchaseModal = (purchase = null) => {
    setEditingPurchase(purchase);
    setShowPurchaseModal(true);
    setError(null);
    setSuccess(null);
  };

  const handleDeleteTrial = async (id) => {
    if (!window.confirm("Are you sure you want to delete this trial definition?")) return;
    
    try {
      await deleteTrialDefinition(id);
      setSuccess("Trial definition deleted successfully");
      loadTrialDefinitions();
    } catch (err) {
      setError("Failed to delete trial definition");
      console.error(err);
    }
  };

  const handleDeletePurchase = async (id) => {
    if (!window.confirm("Are you sure you want to delete this purchase option?")) return;
    
    try {
      await deletePurchaseOption(id);
      setSuccess("Purchase option deleted successfully");
      loadPurchaseOptions();
    } catch (err) {
      setError("Failed to delete purchase option");
      console.error(err);
    }
  };

  return (
    <div>
      <Container fluid className="product-catalog-container">
        <HeadingBanner name={`Welcome ${name}!`} isLoggedIn={true} cloud={true} teacherid={teacherid} />

        <Row className="product-catalog-row">
          <LeftPanel />
          <Col sm={{ span: 10, offset: 2 }} className="product-catalog-content">
            <div className="product-catalog-header">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h2 className="product-catalog-page-title">
                    <Package className="me-2" size={28} />
                    Product Catalog
                  </h2>
                  <p className="product-catalog-page-subtitle">
                    Manage trial definitions and purchase packages
                  </p>
                </div>
              </div>
            </div>

            {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
                {success}
              </Alert>
            )}

            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="mb-4 product-catalog-tabs"
            >
              <Tab
                eventKey="trials"
                title={
                  <span>
                    <Clock size={16} className="me-2" />
                    Trial Definitions
                  </span>
                }
              >
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4>Trial Definitions ({trialDefinitions.length})</h4>
                  <Button
                    variant="primary"
                    onClick={() => handleOpenTrialModal()}
                  >
                    <Plus size={16} className="me-1" />
                    Create New Trial
                  </Button>
                </div>

                {trialsLoading ? (
                  <div className="text-center py-5">Loading trial definitions...</div>
                ) : trialDefinitions.length === 0 ? (
                  <Card className="text-center py-5">
                    <Card.Body>
                      <Clock size={48} className="text-muted mb-3" />
                      <p className="text-muted">No trial definitions yet. Create your first one!</p>
                    </Card.Body>
                  </Card>
                ) : (
                  <div className="product-cards-grid">
                    {trialDefinitions.map((trial) => (
                      <TrialDefinitionCard
                        key={trial.id}
                        trial={trial}
                        countries={countries}
                        onEdit={handleOpenTrialModal}
                        onDelete={handleDeleteTrial}
                      />
                    ))}
                  </div>
                )}
              </Tab>

              <Tab
                eventKey="purchases"
                title={
                  <span>
                    <CreditCard size={16} className="me-2" />
                    Purchase Options
                  </span>
                }
              >
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4>Purchase Options ({purchaseOptions.length})</h4>
                  <Button
                    variant="success"
                    onClick={() => handleOpenPurchaseModal()}
                  >
                    <Plus size={16} className="me-1" />
                    Create New Package
                  </Button>
                </div>

                {purchasesLoading ? (
                  <div className="text-center py-5">Loading purchase options...</div>
                ) : purchaseOptions.length === 0 ? (
                  <Card className="text-center py-5">
                    <Card.Body>
                      <CreditCard size={48} className="text-muted mb-3" />
                      <p className="text-muted">No purchase options yet. Create your first one!</p>
                    </Card.Body>
                  </Card>
                ) : (
                  <div className="product-cards-grid">
                    {purchaseOptions.map((purchase) => (
                      <PurchaseOptionCard
                        key={purchase.id}
                        purchase={purchase}
                        countries={countries}
                        onEdit={handleOpenPurchaseModal}
                        onDelete={handleDeletePurchase}
                      />
                    ))}
                  </div>
                )}
              </Tab>
            </Tabs>
          </Col>
        </Row>
      </Container>

      {isPortrait && <OrientationPrompt />}

      <TrialDefinitionModal
        show={showTrialModal}
        onHide={() => setShowTrialModal(false)}
        trial={editingTrial}
        countries={countries}
        onSuccess={(msg) => {
          setSuccess(msg);
          loadTrialDefinitions();
          setShowTrialModal(false);
        }}
        onError={(msg) => setError(msg)}
      />

      <PurchaseOptionModal
        show={showPurchaseModal}
        onHide={() => setShowPurchaseModal(false)}
        purchase={editingPurchase}
        countries={countries}
        onSuccess={(msg) => {
          setSuccess(msg);
          loadPurchaseOptions();
          setShowPurchaseModal(false);
        }}
        onError={(msg) => setError(msg)}
      />
    </div>
  );
}

// Trial Definition Card Component
function TrialDefinitionCard({ trial, countries, onEdit, onDelete }) {
  const country = countries.find((c) => c.id === trial.countryid);
  const gradeLevelsDisplay = trial.grade_levels ? trial.grade_levels.join(', ') : 'No grades';

  return (
    <Card className="product-card">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <h5 className="product-card-title">{trial.name}</h5>
            <Badge bg={trial.active ? "success" : "secondary"}>
              {trial.active ? "Active" : "Inactive"}
            </Badge>
          </div>
          <div className="product-card-actions">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => onEdit(trial)}
              className="me-1"
            >
              <Edit size={14} />
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => onDelete(trial.id)}
            >
              <Trash2 size={14} />
            </Button>
          </div>
        </div>

        <div className="product-card-details">
          <div className="detail-row">
            <Globe size={14} />
            <span>{country?.name || "Unknown Country"}</span>
          </div>
          <div className="detail-row">
            <Clock size={14} />
            <span>{trial.duration} days</span>
          </div>
          <div className="detail-row">
            <BookOpen size={14} />
            <span>Grades: {gradeLevelsDisplay}</span>
          </div>
          {trial.promo_code && (
            <div className="detail-row">
              <span className="badge bg-info">Code: {trial.promo_code}</span>
            </div>
          )}
          {trial.description && (
            <div className="detail-row">
              <span className="text-muted small">{trial.description}</span>
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}

// Purchase Option Card Component
function PurchaseOptionCard({ purchase, countries, onEdit, onDelete }) {
  const country = countries.find((c) => c.id === purchase.countryid);

  return (
    <Card className="product-card">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <h5 className="product-card-title">{purchase.name}</h5>
            <Badge bg={purchase.active ? "success" : "secondary"}>
              {purchase.active ? "Active" : "Inactive"}
            </Badge>
          </div>
          <div className="product-card-actions">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => onEdit(purchase)}
              className="me-1"
            >
              <Edit size={14} />
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => onDelete(purchase.id)}
            >
              <Trash2 size={14} />
            </Button>
          </div>
        </div>

        <div className="product-card-details">
          <div className="detail-row">
            <Globe size={14} />
            <span>{country?.name || "Unknown Country"}</span>
          </div>
          <div className="detail-row">
            <CreditCard size={14} />
            <span className="fw-bold">{purchase.currency}</span>
          </div>
          {purchase.pricing_tiers && purchase.pricing_tiers.length > 0 && (
            <div className="pricing-tiers-display">
              <strong className="small">Pricing per student:</strong>
              {purchase.pricing_tiers.map((tier, idx) => (
                <div key={idx} className="tier-row">
                  <span className="text-muted small">Grade {tier.grade_level}:</span>
                  <span className="fw-bold small ms-1">
                    {tier.price_per_student === 0 
                      ? 'FREE' 
                      : `${purchase.currency} ${tier.price_per_student}`}
                  </span>
                </div>
              ))}
            </div>
          )}
          {purchase.billing_cycle && (
            <div className="detail-row">
              <span className="text-muted">{purchase.billing_cycle}</span>
            </div>
          )}
          {purchase.description && (
            <div className="detail-row">
              <span className="text-muted small">{purchase.description}</span>
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}

// Trial Definition Modal Component
function TrialDefinitionModal({ show, onHide, trial, countries, onSuccess, onError }) {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    countryid: "",
    grade_levels: "",
    duration: 30,
    promo_code: "",
    screen_window: "",
    active: true,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (trial) {
      setFormData({
        name: trial.name || "",
        description: trial.description || "",
        countryid: trial.countryid || "",
        grade_levels: trial.grade_levels ? trial.grade_levels.join(', ') : "",
        duration: trial.duration || 30,
        promo_code: trial.promo_code || "",
        screen_window: trial.screen_window || "",
        active: trial.active ?? true,
      });
    } else {
      setFormData({
        name: "",
        description: "",
        countryid: "",
        grade_levels: "",
        duration: 30,
        promo_code: "",
        screen_window: "",
        active: true,
      });
    }
  }, [trial, show]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Convert grade_levels string to array of numbers
      const gradeLevelsArray = formData.grade_levels
        .split(',')
        .map(g => parseInt(g.trim()))
        .filter(g => !isNaN(g));

      const submitData = {
        name: formData.name,
        description: formData.description || undefined,
        countryid: formData.countryid,
        grade_levels: gradeLevelsArray,
        duration: parseInt(formData.duration),
        promo_code: formData.promo_code || undefined,
        screen_window: formData.screen_window || undefined,
        active: formData.active,
      };

      console.log("Submitting trial data:", submitData);

      if (trial) {
        await updateTrialDefinition(trial.id, submitData);
        onSuccess("Trial definition updated successfully!");
      } else {
        await createTrialDefinition(submitData);
        onSuccess("Trial definition created successfully!");
      }
    } catch (err) {
      console.error("Full error:", err);
      onError(err.message || "Failed to save trial definition");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {trial ? "Edit Trial Definition" : "Create New Trial Definition"}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Trial Name *</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., 30-Day Junior Infants Trial - Ireland"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Optional description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Country *</Form.Label>
            <Form.Select
              value={formData.countryid}
              onChange={(e) => setFormData({ ...formData, countryid: e.target.value })}
              required
            >
              <option value="">Select Country</option>
              {countries.map((country) => (
                <option key={country.id} value={country.id}>
                  {country.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Grade Levels *</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., 1, 2, 3, 4"
              value={formData.grade_levels}
              onChange={(e) => setFormData({ ...formData, grade_levels: e.target.value })}
              required
            />
            <Form.Text className="text-muted">
              Comma-separated grade level numbers (e.g., 1, 2, 3)
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Duration (Days) *</Form.Label>
            <Form.Control
              type="number"
              min="1"
              value={formData.duration}
              onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Promo Code (Optional)</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., TRIAL30"
              value={formData.promo_code}
              onChange={(e) => setFormData({ ...formData, promo_code: e.target.value })}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Screen Window (Optional)</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., 2024-2025"
              value={formData.screen_window}
              onChange={(e) => setFormData({ ...formData, screen_window: e.target.value })}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Active"
              checked={formData.active}
              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? "Saving..." : trial ? "Update" : "Create"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

// Purchase Option Modal Component
function PurchaseOptionModal({ show, onHide, purchase, countries, onSuccess, onError }) {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    countryid: "",
    currency: "EUR",
    billing_cycle: "annual",
    active: true,
  });
  const [pricingTiers, setPricingTiers] = useState([
    { grade_level: "", price_per_student: "" }
  ]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (purchase) {
      setFormData({
        name: purchase.name || "",
        description: purchase.description || "",
        countryid: purchase.countryid || "",
        currency: purchase.currency || "EUR",
        billing_cycle: purchase.billing_cycle || "annual",
        active: purchase.active ?? true,
      });
      
      // Load existing pricing tiers
      if (purchase.pricing_tiers && purchase.pricing_tiers.length > 0) {
        setPricingTiers(purchase.pricing_tiers.map(tier => ({
          grade_level: tier.grade_level.toString(),
          price_per_student: tier.price_per_student.toString()
        })));
      } else {
        setPricingTiers([{ grade_level: "", price_per_student: "" }]);
      }
    } else {
      setFormData({
        name: "",
        description: "",
        countryid: "",
        currency: "EUR",
        billing_cycle: "annual",
        active: true,
      });
      setPricingTiers([{ grade_level: "", price_per_student: "" }]);
    }
  }, [purchase, show]);

  const handleAddTier = () => {
    setPricingTiers([...pricingTiers, { grade_level: "", price_per_student: "" }]);
  };

  const handleRemoveTier = (index) => {
    if (pricingTiers.length > 1) {
      setPricingTiers(pricingTiers.filter((_, i) => i !== index));
    }
  };

  const handleTierChange = (index, field, value) => {
    const updatedTiers = [...pricingTiers];
    updatedTiers[index][field] = value;
    setPricingTiers(updatedTiers);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Convert pricing tiers to proper format
      const validTiers = pricingTiers
        .filter(tier => tier.grade_level !== "" && tier.price_per_student !== "")
        .map(tier => ({
          grade_level: parseInt(tier.grade_level),
          price_per_student: parseFloat(tier.price_per_student)
        }));

      if (validTiers.length === 0) {
        onError("Please add at least one pricing tier");
        setSubmitting(false);
        return;
      }

      // Extract grade levels from pricing tiers
      const gradeLevelsArray = validTiers.map(tier => tier.grade_level);

      const submitData = {
        name: formData.name,
        description: formData.description || undefined,
        countryid: formData.countryid,
        grade_levels: gradeLevelsArray,
        pricing_tiers: validTiers,
        currency: formData.currency,
        billing_cycle: formData.billing_cycle,
        active: formData.active,
      };

      console.log("Submitting purchase data:", submitData);

      if (purchase) {
        await updatePurchaseOption(purchase.id, submitData);
        onSuccess("Purchase option updated successfully!");
      } else {
        await createPurchaseOption(submitData);
        onSuccess("Purchase option created successfully!");
      }
    } catch (err) {
      console.error("Full error:", err);
      onError(err.message || "Failed to save purchase option");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {purchase ? "Edit Purchase Option" : "Create New Purchase Option"}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Package Name *</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., JI Primary Package with SI Discount"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Optional description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Country *</Form.Label>
            <Form.Select
              value={formData.countryid}
              onChange={(e) => setFormData({ ...formData, countryid: e.target.value })}
              required
            >
              <option value="">Select Country</option>
              {countries.map((country) => (
                <option key={country.id} value={country.id}>
                  {country.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Currency *</Form.Label>
                <Form.Select
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  required
                >
                  <option value="EUR">EUR (€)</option>
                  <option value="GBP">GBP (£)</option>
                  <option value="USD">USD ($)</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Billing Cycle *</Form.Label>
                <Form.Select
                  value={formData.billing_cycle}
                  onChange={(e) => setFormData({ ...formData, billing_cycle: e.target.value })}
                  required
                >
                  <option value="annual">Annual</option>
                  <option value="monthly">Monthly</option>
                  <option value="one-time">One-time</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Form.Label className="mb-0">Pricing Tiers (Per Grade) *</Form.Label>
              <Button 
                variant="outline-primary" 
                size="sm" 
                onClick={handleAddTier}
                type="button"
              >
                <Plus size={14} className="me-1" />
                Add Grade
              </Button>
            </div>
            <Form.Text className="text-muted d-block mb-2">
              Set price per student for each grade level. Use 0 for free grades.
            </Form.Text>
            
            {pricingTiers.map((tier, index) => (
              <Row key={index} className="mb-2 align-items-center">
                <Col xs={5}>
                  <Form.Control
                    type="number"
                    placeholder="Grade level (e.g., 1)"
                    value={tier.grade_level}
                    onChange={(e) => handleTierChange(index, 'grade_level', e.target.value)}
                    min="1"
                    required
                  />
                </Col>
                <Col xs={5}>
                  <Form.Control
                    type="number"
                    step="0.01"
                    placeholder="Price per student"
                    value={tier.price_per_student}
                    onChange={(e) => handleTierChange(index, 'price_per_student', e.target.value)}
                    min="0"
                    required
                  />
                </Col>
                <Col xs={2}>
                  {pricingTiers.length > 1 && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleRemoveTier(index)}
                      type="button"
                    >
                      <Trash2 size={14} />
                    </Button>
                  )}
                </Col>
              </Row>
            ))}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Active"
              checked={formData.active}
              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="success" type="submit" disabled={submitting}>
            {submitting ? "Saving..." : purchase ? "Update" : "Create"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default ProductCatalog;